import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
  Tabs,
} from '@/components/ui'
import { fetchColaboradores } from '@/features/rh/api'
import {
  alternarBeneficioStatus,
  atribuirBeneficio,
  fetchBeneficiosCatalogo,
  fetchBeneficiosRelatorio,
  fetchBeneficiosVinculos,
  removerVinculoBeneficio,
} from './api'
import { formatDate, formatMoney } from './utils'

type Tab = 'catalogo' | 'vinculos' | 'custo' | 'politica'

function num(v: unknown) {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

export function BeneficiosPage() {
  const { canEdit } = useAuth()
  const editable = canEdit('dp-beneficios')
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('catalogo')
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState<'success' | 'danger' | 'info'>('info')

  const [atribuirOpen, setAtribuirOpen] = useState(false)
  const [colabId, setColabId] = useState('')
  const [benId, setBenId] = useState('')
  const [buscaColab, setBuscaColab] = useState('')

  const catalogo = useQuery({
    queryKey: ['dp', 'beneficios', 'catalogo'],
    queryFn: () => fetchBeneficiosCatalogo(true),
  })

  const vinculos = useQuery({
    queryKey: ['dp', 'beneficios', 'vinculos'],
    queryFn: fetchBeneficiosVinculos,
    enabled: tab === 'vinculos' || tab === 'custo' || atribuirOpen,
  })

  const relatorio = useQuery({
    queryKey: ['dp', 'beneficios', 'relatorio'],
    queryFn: fetchBeneficiosRelatorio,
    enabled: tab === 'custo' || tab === 'catalogo',
  })

  const colabs = useQuery({
    queryKey: ['dp', 'beneficios', 'colabs', buscaColab],
    queryFn: () =>
      fetchColaboradores({
        busca: buscaColab || undefined,
        status: 'ativo',
        por_pagina: 40,
      }),
    enabled: atribuirOpen,
  })

  const bens = catalogo.data?.dados || []
  const vincs = vinculos.data?.dados || []
  const rel = (relatorio.data?.dados || {}) as Record<string, unknown>
  const porTipo = (Array.isArray(rel.por_tipo) ? rel.por_tipo : []) as Array<
    Record<string, unknown>
  >
  const maxCusto = Math.max(...porTipo.map((t) => num(t.custo_empresa)), 1)
  const ativos = bens.filter((b) => b.ativo !== false).length

  const kpis = useMemo(
    () => ({
      custoEmp: num(rel.total_custo_empresa),
      custoColab: num(rel.total_custo_colaborador),
      nColabs: num(rel.colaboradores_com_beneficio) || new Set(vincs.map((v) => v.colaborador)).size,
      medio: num(rel.custo_medio_por_colaborador),
    }),
    [rel, vincs],
  )

  function flash(text: string, tone: 'success' | 'danger' | 'info' = 'success') {
    setMsgTone(tone)
    setMsg(text)
  }

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ['dp', 'beneficios'] })
  }

  const toggleMut = useMutation({
    mutationFn: (id: string) => alternarBeneficioStatus(id),
    onSuccess: async () => {
      flash('Status do benefício atualizado.')
      await invalidate()
    },
    onError: (err) => flash(err instanceof ApiError ? err.message : 'Falha ao alterar.', 'danger'),
  })

  const atribuirMut = useMutation({
    mutationFn: () =>
      atribuirBeneficio({
        colaborador_id: colabId,
        beneficio_id: benId,
        data_inicio: new Date().toISOString().slice(0, 10),
      }),
    onSuccess: async () => {
      flash('Benefício atribuído.')
      setAtribuirOpen(false)
      setColabId('')
      setBenId('')
      await invalidate()
    },
    onError: (err) => flash(err instanceof ApiError ? err.message : 'Falha ao atribuir.', 'danger'),
  })

  const removerMut = useMutation({
    mutationFn: (id: string) => removerVinculoBeneficio(id),
    onSuccess: async () => {
      flash('Vínculo removido.', 'info')
      await invalidate()
    },
    onError: (err) => flash(err instanceof ApiError ? err.message : 'Falha ao remover.', 'danger'),
  })

  return (
    <section className="hi-page">
      <PageHeader
        title="Gestão de benefícios"
        subtitle="Catálogo, vínculos, custo e política — ideias do legado na SPA."
        actions={
          <>
            <a className="btn btn-secondary" href={legacyAppUrl('#/dp-beneficios')}>
              Legado
            </a>
            {editable ? (
              <button type="button" className="btn btn-primary" onClick={() => setAtribuirOpen(true)}>
                Atribuir benefício
              </button>
            ) : null}
          </>
        }
      />

      {msg ? <Alert tone={msgTone}>{msg}</Alert> : null}

      <KpiGrid>
        <Kpi label="Custo empresa/mês" value={formatMoney(kpis.custoEmp)} />
        <Kpi label="Benefícios ativos" value={String(ativos)} />
        <Kpi label="Colaboradores" value={String(kpis.nColabs)} />
        <Kpi label="Custo médio/colab" value={formatMoney(kpis.medio)} />
      </KpiGrid>

      <Tabs
        items={[
          { id: 'catalogo', label: 'Catálogo' },
          { id: 'vinculos', label: 'Por colaborador' },
          { id: 'custo', label: 'Análise de custo' },
          { id: 'politica', label: 'Política' },
        ]}
        value={tab}
        onChange={setTab}
        label="Abas de benefícios"
      />

      {tab === 'catalogo' ? (
        <Section title="Catálogo" flush>
          {catalogo.isLoading ? <Muted>Carregando…</Muted> : null}
          {catalogo.isError ? (
            <Alert tone="danger">
              {catalogo.error instanceof ApiError ? catalogo.error.message : 'Falha no catálogo.'}
            </Alert>
          ) : null}
          {!catalogo.isLoading && bens.length === 0 ? <Empty>Catálogo vazio.</Empty> : null}
          {bens.length > 0 ? (
            <DataTable>
              <thead>
                <tr>
                  <th>Benefício</th>
                  <th>Tipo</th>
                  <th>Empresa</th>
                  <th>Colaborador</th>
                  <th>Elegibilidade</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {bens.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <strong>{b.nome}</strong>
                      {b.fornecedor ? (
                        <>
                          <br />
                          <Muted>{b.fornecedor}</Muted>
                        </>
                      ) : null}
                    </td>
                    <td>{b.tipo_display || b.tipo || '—'}</td>
                    <td>{formatMoney(b.valor_empresa)}</td>
                    <td>{formatMoney(b.valor_colab)}</td>
                    <td>
                      <Muted>{(b as { elegibilidade?: string }).elegibilidade || '—'}</Muted>
                    </td>
                    <td>
                      <StatusBadge tone={b.ativo === false ? 'inativo' : 'ativo'}>
                        {b.ativo === false ? 'Inativo' : 'Ativo'}
                      </StatusBadge>
                    </td>
                    <td>
                      {editable ? (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={toggleMut.isPending}
                          onClick={() => toggleMut.mutate(b.id)}
                        >
                          Alternar
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : null}
        </Section>
      ) : null}

      {tab === 'vinculos' ? (
        <Section title="Vínculos com colaboradores" flush>
          {vinculos.isLoading ? <Muted>Carregando…</Muted> : null}
          {vinculos.isError ? (
            <Alert tone="danger">
              {vinculos.error instanceof ApiError ? vinculos.error.message : 'Falha nos vínculos.'}
            </Alert>
          ) : null}
          {!vinculos.isLoading && vincs.length === 0 ? <Empty>Nenhum vínculo.</Empty> : null}
          {vincs.length > 0 ? (
            <DataTable>
              <thead>
                <tr>
                  <th>Colaborador</th>
                  <th>Benefício</th>
                  <th>Início</th>
                  <th>Empresa</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {vincs.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <strong>{v.colaborador_nome || '—'}</strong>
                      {v.colaborador_cargo ? (
                        <>
                          <br />
                          <Muted>{v.colaborador_cargo}</Muted>
                        </>
                      ) : null}
                    </td>
                    <td>{v.beneficio_nome || '—'}</td>
                    <td>{formatDate(v.data_inicio)}</td>
                    <td>{formatMoney(v.valor_empresa)}</td>
                    <td>
                      <StatusBadge
                        tone={/ativ|ativo/i.test(v.status || '') ? 'ativo' : 'pendente'}
                      >
                        {v.status_display || v.status || '—'}
                      </StatusBadge>
                    </td>
                    <td>
                      {editable ? (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={removerMut.isPending}
                          onClick={() => removerMut.mutate(v.id)}
                        >
                          Remover
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : null}
        </Section>
      ) : null}

      {tab === 'custo' ? (
        <div className="hi-stack">
          {relatorio.isLoading ? <Muted>Carregando custos…</Muted> : null}
          {relatorio.isError ? (
            <Alert tone="danger">
              {relatorio.error instanceof ApiError
                ? relatorio.error.message
                : 'Falha no relatório de custos.'}
            </Alert>
          ) : null}
          <div className="hi-ferias-grid-2">
            <Section title="Custo mensal por benefício">
              {porTipo.length === 0 ? (
                <Empty>Sem breakdown por tipo.</Empty>
              ) : (
                <div className="hi-ferias-bars">
                  {porTipo.map((t) => {
                    const total = num(t.custo_empresa)
                    const cols = Math.max(num(t.colaboradores), 1)
                    const nome = String(t.beneficio__nome || t.nome || 'Benefício')
                    return (
                      <div key={nome} style={{ marginBottom: '0.75rem' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: 4,
                          }}
                        >
                          <strong style={{ fontSize: '0.85rem' }}>{nome}</strong>
                          <span>{formatMoney(total)}</span>
                        </div>
                        <div className="hi-ferias-bar-track">
                          <div
                            className="hi-ferias-bar-fill"
                            style={{ width: `${(total / maxCusto) * 100}%` }}
                          />
                        </div>
                        <Muted>
                          {cols} colaborador(es) · {formatMoney(total / cols)}/colab
                        </Muted>
                      </div>
                    )
                  })}
                </div>
              )}
            </Section>
            <Section title="Resumo financeiro">
              {[
                ['Custo empresa/mês', formatMoney(kpis.custoEmp)],
                ['Descontos colaboradores', formatMoney(kpis.custoColab)],
                ['Custo médio/colab', formatMoney(kpis.medio)],
                ['Colaboradores cobertos', String(kpis.nColabs)],
                ['Tipos no relatório', String(porTipo.length)],
                ['Ativos no catálogo', String(ativos)],
              ].map(([label, val]) => (
                <div
                  key={label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.45rem 0',
                    borderBottom: '1px solid var(--hi-border, #eef1f6)',
                  }}
                >
                  <Muted>{label}</Muted>
                  <strong>{val}</strong>
                </div>
              ))}
            </Section>
          </div>
        </div>
      ) : null}

      {tab === 'politica' ? (
        <div className="hi-ferias-grid-2">
          <Section title="Política de benefícios">
            <p>
              <strong>Elegibilidade</strong>
            </p>
            <ul>
              <li>Colaboradores CLT a partir do 1º dia útil</li>
              <li>Estagiários: VA, VR e VT apenas</li>
              <li>PJ/Prestadores: excluídos do pacote</li>
              <li>Diretores: pacote executivo diferenciado</li>
            </ul>
            <p>
              <strong>Carência</strong>
            </p>
            <ul>
              <li>VA/VR/VT: sem carência</li>
              <li>Plano de saúde: 30 dias (urgências imediatas)</li>
              <li>Plano odonto: 180 dias para eletivos</li>
              <li>Seguro de vida: vigência imediata</li>
            </ul>
            <p>
              <strong>Dependentes</strong>
            </p>
            <ul>
              <li>Cônjuge/companheiro(a) com união estável</li>
              <li>Filhos até 24 anos (estudantes) ou sem limite (PCD)</li>
              <li>Documentação obrigatória para inclusão</li>
            </ul>
          </Section>
          <Section title="Janelas de alteração">
            {[
              ['Inclusão de dependente', '30 dias após evento'],
              ['Exclusão de dependente', 'A qualquer momento'],
              ['Mudança de plano', 'Aniversário do plano'],
              ['Cancelamento voluntário', 'Início de cada mês'],
            ].map(([label, val]) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.45rem 0',
                  borderBottom: '1px solid var(--hi-border, #eef1f6)',
                }}
              >
                <span>{label}</span>
                <strong>{val}</strong>
              </div>
            ))}
            <Alert tone="info">
              Texto de política espelhado do legado. Ajuste fino por empresa pode virar config
              depois.
            </Alert>
          </Section>
        </div>
      ) : null}

      {atribuirOpen ? (
        <div
          className="hi-ferias-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setAtribuirOpen(false)
          }}
        >
          <div className="hi-ferias-modal" role="dialog" aria-modal="true">
            <div className="hi-ferias-modal-head">
              <h3>Atribuir benefício</h3>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setAtribuirOpen(false)}
              >
                Fechar
              </button>
            </div>
            <label className="hi-field">
              <span>Buscar colaborador</span>
              <input
                value={buscaColab}
                onChange={(e) => setBuscaColab(e.target.value)}
                placeholder="Nome…"
              />
            </label>
            <label className="hi-field">
              <span>Colaborador</span>
              <select value={colabId} onChange={(e) => setColabId(e.target.value)}>
                <option value="">Selecione…</option>
                {(colabs.data?.dados || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </label>
            <label className="hi-field">
              <span>Benefício</span>
              <select value={benId} onChange={(e) => setBenId(e.target.value)}>
                <option value="">Selecione…</option>
                {bens
                  .filter((b) => b.ativo !== false)
                  .map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.nome}
                    </option>
                  ))}
              </select>
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setAtribuirOpen(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!colabId || !benId || atribuirMut.isPending}
                onClick={() => atribuirMut.mutate()}
              >
                {atribuirMut.isPending ? 'Salvando…' : 'Atribuir'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}

export default BeneficiosPage
